import { useCallback, useEffect, useState } from "react";
import { KeyRound, Plus, Trash2, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Field, Panel, TextInput } from "@/components/admin/Field";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const arr = new Uint32Array(12);
  window.crypto.getRandomValues(arr);
  return "Eden-" + Array.from(arr, (n) => chars[n % chars.length]).join("");
}

function NewAdminForm({ onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", password: generatePassword() });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Conta criada para ${form.email}. Envia-lhe a password temporária em privado.`);
      onCreated();
      setForm({ email: "", name: "", password: generatePassword() });
    } catch (err) {
      toast.error(errorMessage(err, "Não foi possível criar a conta."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3" data-testid="new-admin-form">
      <Field label="Email" id="new-admin-email"><input id="new-admin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-eden" data-testid="input-new-admin-email" /></Field>
      <Field label="Nome (opcional)" id="new-admin-name"><TextInput id="new-admin-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={80} /></Field>
      <Field label="Password temporária" id="new-admin-password" hint="Mín. 10 caracteres, letras e números. A pessoa deve mudá-la em Conta.">
        <div className="flex gap-2">
          <TextInput id="new-admin-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} minLength={10} maxLength={128} />
          <button type="button" className="btn-ghost h-10 shrink-0 px-3" onClick={() => setForm({ ...form, password: generatePassword() })} aria-label="Gerar nova password" data-testid="generate-password-button"><KeyRound size={14} /></button>
        </div>
      </Field>
      <div className="sm:col-span-3"><button type="submit" disabled={busy} className="btn-primary h-10" data-testid="create-admin-submit"><Plus size={16} /> {busy ? "A criar…" : "Criar conta de administrador"}</button></div>
    </form>
  );
}

function AdminRow({ u, me, reload }) {
  const isOwner = u.role === "owner";
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const resetPassword = () => {
    const pw = generatePassword();
    act(() => api.post(`/admin/users/${u.id}/reset-password`, { password: pw }), `Nova password temporária para ${u.email}: ${pw}`);
  };
  return (
    <li className={`flex flex-wrap items-center gap-4 px-5 py-4 ${u.active === false ? "opacity-60" : ""}`} data-testid={`admin-user-${u.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-eden-text">{u.name || u.email} {u.id === me?.id && <span className="text-xs text-eden-muted">(tu)</span>}</p>
        <p className="truncate text-xs text-eden-muted">{u.email} · {isOwner ? "Fundadora" : "Administrador"}{u.active === false ? " · desativado" : ""}{u.last_login_at ? ` · último acesso ${formatDate(u.last_login_at)}` : " · nunca entrou"}</p>
      </div>
      {!isOwner && (
        <div className="flex gap-1">
          <button type="button" className="btn-ghost h-8 px-2" onClick={() => act(() => api.put(`/admin/users/${u.id}`, { active: u.active === false }), u.active === false ? "Conta reativada." : "Conta desativada.")} aria-label={u.active === false ? "Reativar conta" : "Desativar conta"} data-testid={`admin-user-toggle-${u.id}`}>
            {u.active === false ? <UserCheck size={14} /> : <UserX size={14} />}
          </button>
          <button type="button" className="btn-ghost h-8 px-2" onClick={resetPassword} aria-label="Gerar nova password" data-testid={`admin-user-reset-${u.id}`}><KeyRound size={14} /></button>
          <ConfirmDelete title="Eliminar administrador?" description={`${u.email} deixa de conseguir entrar no painel. Esta ação não pode ser anulada.`} onConfirm={() => act(() => api.delete(`/admin/users/${u.id}`), "Conta eliminada.")} testId={`admin-user-delete-${u.id}`}>
            <button type="button" className="btn-ghost h-8 px-2 text-red-400 hover:text-red-300" aria-label="Eliminar conta" data-testid={`admin-user-delete-${u.id}`}><Trash2 size={14} /></button>
          </ConfirmDelete>
        </div>
      )}
    </li>
  );
}

export default function EditEquipa() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data.users)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Panel title="Nova conta de administrador" description="Cria contas para outros membros da staff. Eles podem editar todo o conteúdo, mas só tu podes gerir a equipa.">
        <NewAdminForm onCreated={load} />
      </Panel>
      <Panel title="Equipa" description="Contas com acesso ao painel de gestão.">
        {!users && <p className="text-sm text-eden-muted">A carregar…</p>}
        {users && (
          <ul className="-mx-6 divide-y divide-eden-borderSubtle border-y border-eden-border sm:-mx-8" data-testid="admin-users-list">
            {users.map((u) => <AdminRow key={u.id} u={u} me={user} reload={load} />)}
          </ul>
        )}
      </Panel>
    </>
  );
}
